import * as Sentry from "@sentry/react";
import { supabase } from "./supabase";

const API_URL = import.meta.env.VITE_GO_API_URL ?? "http://localhost:8080";

async function getAuthHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) return {};
  return { Authorization: `Bearer ${session.access_token}` };
}

export async function goFetch<T>(
  path: string,
  options?: {
    auth?: boolean;
    params?: Record<string, string>;
    method?: string;
    body?: unknown;
  }
): Promise<T> {
  const { auth = true, params, method = "GET", body } = options ?? {};

  let url = `${API_URL}${path}`;
  if (params) {
    url += "?" + new URLSearchParams(params).toString();
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (auth) {
    Object.assign(headers, await getAuthHeaders());
  }
  const res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const respBody = await res.json().catch(() => ({}));
    const message = respBody.error ?? `API error ${res.status}`;
    if (res.status >= 500) {
      Sentry.captureException(new Error(message), {
        tags: { endpoint: path, status: String(res.status) },
      });
    }
    throw new Error(message);
  }
  return res.json();
}

/** Fetch a binary response (e.g. a PDF) with the auth header and hand it to the browser as a download. */
export async function goDownload(path: string, fallbackFilename: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}${path}`, { headers });
  if (!res.ok) {
    const respBody = await res.json().catch(() => ({}));
    throw new Error(respBody.error ?? `API error ${res.status}`);
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = disposition.match(/filename="([^"]+)"/);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = match?.[1] ?? fallbackFilename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
